import { useEffect, useRef, useState } from 'react';
import { BRANDS, SOCIAL_ASSET_CHANNELS, SOCIAL_ASSET_TYPES, type SocialAssetWithProject } from '../../../shared/types';
import { formatWeekdayDate, formatWeekdayDay } from '../../lib/calendar';
import { DOW, STATUS_FILTER_ORDER, STATUS_LABELS, TYPE_LABELS, assetChannels, daySlots, defaultSelectedDay, filterAssets, monthLabel, shiftMonth, slotDot, type CalendarWeek, type ChannelFilter, type ScheduleDraft, type SlotDot, type SocialActions } from '../../lib/socialCalendar';
import { SkeletonRows } from '../primitives';
import { PickerSheet } from '../PickerSheet';
import { DateSheet } from '../DateSheet';
import { type SubView, type SocialMobileProps } from './types';
import { LibraryRow } from './LibraryRow';
import { AssetSheet } from './AssetSheet';
import { ScheduleSheet } from './ScheduleSheet';
import { SlotActionsSheet } from './SlotActionsSheet';

/** Mockup dot classes: teal HSP is the bare `i`, SS gold, used grey, prep
    outlined. One class each so the CSS stays a single rule line. */
const DOT_CLASS: Record<SlotDot, string> = { hsp: '', ss: 'ss', used: 'done', prep: 'prep' };

const CHANNEL_CHIP: Record<ChannelFilter, string> = { all: 'All', ig: 'IG', linkedin: 'LI' };

export default function SocialMobile({
  assets,
  loaded,
  today,
  counters,
  view,
  onView,
  filters,
  onFilter,
  openId,
  month,
  onMonth,
  channel,
  onChannel,
  weeks,
  ready,
  actions,
  onSchedule,
  empty,
}: SocialMobileProps) {
  const [channelPicker, setChannelPicker] = useState(false);
  const [selectedDay, setSelectedDay] = useState<string | null>(() => defaultSelectedDay(weeks, today));
  const [slotFor, setSlotFor] = useState<string | null>(null);
  const [moveDate, setMoveDate] = useState<SocialAssetWithProject | null>(null);
  const [fillWeek, setFillWeek] = useState<CalendarWeek | null>(null);
  const [draft, setDraft] = useState<ScheduleDraft | null>(null);
  // Re-seed the selected day when the month changes, never when the assets
  // refetch — a mutation must not throw the reader back to today.
  const shownMonth = useRef(month);
  useEffect(() => {
    if (shownMonth.current === month) return;
    shownMonth.current = month;
    setSelectedDay(defaultSelectedDay(weeks, today));
  }, [month, weeks, today]);

  const openSchedule = (asset: SocialAssetWithProject, date?: string) => {
    // A used post never re-enters the plan — that would strand `used_at`
    // against a live slot (same guard the desktop page holds).
    if (asset.status === 'used') return;
    setSlotFor(null);
    setFillWeek(null);
    setDraft({
      asset,
      date: date ?? asset.planned_date ?? today,
      channels: assetChannels(asset.channels).length > 0
        ? assetChannels(asset.channels)
        : [...SOCIAL_ASSET_CHANNELS],
    });
  };

  // Every "schedule" affordance below — the row button, the tray card, the
  // asset sheet, gap fill — opens the sheet, not the desktop popover the
  // page's own onStartSchedule drives.
  const mobileActions: SocialActions = { ...actions, onStartSchedule: openSchedule };

  const rows = filterAssets(assets, filters);
  const openAsset = openId === null ? null : assets.find((a) => a.id === openId) ?? null;
  const slotAsset = slotFor === null ? null : assets.find((a) => a.id === slotFor) ?? null;
  const days = weeks.flatMap((w) => w.days);
  const selected = selectedDay === null ? null : days.find((d) => d.iso === selectedDay) ?? null;
  const slots = selected === null ? [] : daySlots(selected.slots, selected.iso);

  return (
    <>
      <div className="mtabs">
        {([
          { value: 'calendar' as SubView, label: 'Calendar' },
          { value: 'library' as SubView, label: 'Library' },
        ]).map((tab) => (
          <button
            type="button"
            key={tab.value}
            className={`t${view === tab.value ? ' on' : ''}`}
            onClick={() => onView(tab.value)}
          >
            {tab.label}
          </button>
        ))}
        {view === 'calendar' && (
          <button type="button" className="t social-m-chn" onClick={() => setChannelPicker(true)}>
            {CHANNEL_CHIP[channel]} ▾
          </button>
        )}
      </div>

      <div className="page-body">
        <div className="content wide social-m">
          {!loaded ? (
            <SkeletonRows rows={6} />
          ) : assets.length === 0 ? (
            empty
          ) : view === 'library' ? (
            <>
              <div className="social-m-ctr">
                <div className="c warn">
                  <div className="n">{counters.planned}</div>
                  <div className="k">planned · 14d</div>
                </div>
                <div className="c good">
                  <div className="n">{counters.ready}</div>
                  <div className="k">ready</div>
                </div>
                <div className="c warn">
                  <div className="n">{counters.needsPrep}</div>
                  <div className="k">needs prep</div>
                </div>
                <div className="c bad">
                  <div className="n">{counters.restricted}</div>
                  <div className="k">restricted</div>
                </div>
              </div>

              <div className="social-m-chips">
                {STATUS_FILTER_ORDER.map((status) => (
                  <button
                    type="button"
                    key={status}
                    className={`c${filters.status === status ? ' on' : ''}`}
                    aria-pressed={filters.status === status}
                    onClick={() =>
                      onFilter({ ...filters, status: filters.status === status ? null : status })
                    }
                  >
                    {STATUS_LABELS[status]}
                  </button>
                ))}
                <span className="sep" />
                {BRANDS.map((brand) => (
                  <button
                    type="button"
                    key={brand}
                    className={`c${filters.brand === brand ? ' on' : ''}`}
                    aria-pressed={filters.brand === brand}
                    onClick={() => onFilter({ ...filters, brand: filters.brand === brand ? null : brand })}
                  >
                    {brand}
                  </button>
                ))}
                <span className="sep" />
                {SOCIAL_ASSET_TYPES.map((type) => (
                  <button
                    type="button"
                    key={type}
                    className={`c${filters.type === type ? ' on' : ''}`}
                    aria-pressed={filters.type === type}
                    onClick={() => onFilter({ ...filters, type: filters.type === type ? null : type })}
                  >
                    {TYPE_LABELS[type]}
                  </button>
                ))}
              </div>

              {rows.map((asset) => (
                <LibraryRow
                  key={asset.id}
                  asset={asset}
                  actions={mobileActions}
                  onOpen={() => actions.onOpenAsset(asset.id)}
                />
              ))}
              {rows.length === 0 && (
                <div className="social-m-blank">Nothing matches these filters.</div>
              )}
            </>
          ) : (
            <>
              <div className="social-m-mh">
                <button type="button" aria-label="Previous month" onClick={() => onMonth(shiftMonth(month, -1))}>
                  ‹
                </button>
                <span className="m">{monthLabel(month)}</span>
                <button type="button" aria-label="Next month" onClick={() => onMonth(shiftMonth(month, 1))}>
                  ›
                </button>
                <button
                  type="button"
                  className="today"
                  onClick={() => {
                    onMonth(today.slice(0, 7));
                    setSelectedDay(today);
                  }}
                >
                  Today
                </button>
              </div>

              <div className="social-mgrid">
                <div className="dow">
                  {DOW.map((d) => (
                    <span key={d}>{d}</span>
                  ))}
                </div>
                {weeks.map((week) =>
                  week.gap ? (
                    <div className="wk" key={week.start}>
                      <div className="gap">
                        {/* The desktop bar adds "· nothing planned"; at 393px
                            that truncates the dates, and a dashed empty week
                            already says it. */}
                        <span>
                          Gap · {formatWeekdayDay(week.start)} to {formatWeekdayDay(week.end)}
                        </span>
                        {ready.length > 0 && week.end >= today && (
                          <button type="button" onClick={() => setFillWeek(week)}>
                            fill from {ready.length} ready
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="wk" key={week.start}>
                      {week.days.map((day) => (
                        <button
                          type="button"
                          key={day.iso}
                          className={`dy${day.inMonth ? '' : ' o'}${day.isToday ? ' t' : ''}${day.iso === selectedDay ? ' sel' : ''}`}
                          aria-pressed={day.iso === selectedDay}
                          aria-label={`${formatWeekdayDate(day.iso)}${day.slots.length > 0 ? ` · ${day.slots.length} planned` : ''}`}
                          onClick={() => setSelectedDay(day.iso)}
                        >
                          {day.day}
                          {day.slots.length > 0 && (
                            <span className="s">
                              {day.slots.map((a) => (
                                <i key={a.id} className={DOT_CLASS[slotDot(a)]} />
                              ))}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  ),
                )}
              </div>

              {selected !== null && (
                <>
                  <div className="social-m-gh">
                    {formatWeekdayDate(selected.iso)}
                    <span className="n">{slots.length} planned</span>
                  </div>
                  {slots.map((slot) => (
                    <button
                      type="button"
                      key={slot.id}
                      className="social-m-slot"
                      onClick={() => setSlotFor(slot.id)}
                    >
                      <span className={`pill ${slot.channels.includes('ig') ? 'gold' : 'accent'}`}>
                        {slot.label}
                      </span>
                      <span className="social-m-main">
                        <span className={`social-m-ttl${slot.used ? ' used' : ''}`}>
                          {slot.asset.label}
                        </span>
                        <span className="social-m-meta">
                          <span className="social-m-proj">{slot.asset.project_name ?? 'standalone'}</span>
                          <span className="social-m-sep">·</span>
                          {slot.asset.brand}
                        </span>
                      </span>
                      <span className="chev">›</span>
                    </button>
                  ))}
                  {slots.length === 0 && <div className="social-m-blank">Nothing planned this day.</div>}
                </>
              )}

              <div className="social-m-gh">
                Ready to slot
                <span className="n">{ready.length}</span>
                <span className="a">tap to place</span>
              </div>
              {ready.length === 0 ? (
                <div className="social-m-blank">Nothing ready to slot.</div>
              ) : (
                <div className="social-m-tray">
                  {ready.map((asset) => (
                    <button
                      type="button"
                      className="tc"
                      key={asset.id}
                      onClick={() => openSchedule(asset, selectedDay ?? undefined)}
                    >
                      <span className="pill social-type">{TYPE_LABELS[asset.type]}</span>
                      <span className="l">{asset.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {openAsset !== null && (
        <AssetSheet
          asset={openAsset}
          actions={mobileActions}
          onSchedule={() => {
            actions.onOpenAsset(openAsset.id);
            openSchedule(openAsset);
          }}
          onClose={() => actions.onOpenAsset(openAsset.id)}
        />
      )}

      {slotAsset !== null && (
        <SlotActionsSheet
          asset={slotAsset}
          actions={mobileActions}
          onMoveDate={() => setMoveDate(slotAsset)}
          onEditChannels={() => openSchedule(slotAsset, slotAsset.planned_date ?? undefined)}
          onClose={() => setSlotFor(null)}
        />
      )}

      <DateSheet
        open={moveDate !== null}
        title="Move date"
        context={moveDate?.label}
        value={moveDate?.planned_date ?? null}
        verb="Move to"
        onPick={(iso) => {
          if (moveDate === null || iso === null) return;
          onSchedule({ asset: moveDate, date: iso, channels: assetChannels(moveDate.channels) });
        }}
        onClose={() => setMoveDate(null)}
      />

      {fillWeek !== null && (
        <PickerSheet
          open
          title={`Fill ${formatWeekdayDay(fillWeek.start)} to ${formatWeekdayDay(fillWeek.end)}`}
          items={ready.map((a) => ({
            value: a.id,
            label: a.label,
            sub: `${TYPE_LABELS[a.type]} · ${a.project_name ?? 'standalone'} · ${a.brand}`,
          }))}
          onSelect={(id) => {
            const asset = ready.find((a) => a.id === id);
            // A gap week that has already started fills from today, not from
            // a Monday in the past (the desktop popover does the same).
            const date = fillWeek.start >= today ? fillWeek.start : today;
            setFillWeek(null);
            if (asset) openSchedule(asset, date);
          }}
          onClose={() => setFillWeek(null)}
        />
      )}

      {draft !== null && (
        <ScheduleSheet
          draft={draft}
          assets={assets}
          today={today}
          onChange={setDraft}
          onConfirm={() => {
            onSchedule(draft);
            setDraft(null);
          }}
          onClose={() => setDraft(null)}
        />
      )}

      <PickerSheet
        open={channelPicker}
        title="Channel"
        items={[
          { value: 'all' as ChannelFilter, label: 'All channels' },
          { value: 'ig' as ChannelFilter, label: 'Instagram' },
          { value: 'linkedin' as ChannelFilter, label: 'LinkedIn' },
        ]}
        value={channel}
        onSelect={(next) => onChannel(next)}
        onClose={() => setChannelPicker(false)}
      />
    </>
  );
}
